import { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Form,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Switch,
  Button,
  Space,
  Typography,
  Empty,
  Alert,
  Tag,
  Divider,
} from 'antd';
import { MedicineBoxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  AjccStage,
  HistoTumorType,
  type Diagnosis,
  type CreateDiagnosisDto,
} from '../../../../api/dermatoApi';
import { useAuthStore } from '../../../../store/authStore';
import { useDermatoWizard, useRegisterStepSave } from '../DermatoWizardContext';
import dermatoApi from '../../../../api/dermatoApi';

const { Text } = Typography;

const AJCC_OPTIONS = Object.values(AjccStage).map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));
const TUMOR_OPTIONS = Object.values(HistoTumorType).map((v) => ({
  value: v,
  label:
    v === 'BENIGN_NEVUS' ? 'Nevus benign'
    : v === 'MELANOMA_MALIGNANT' ? 'Melanom malign'
    : v === 'BASAL_CELL_CARCINOMA' ? 'Carcinom bazocelular'
    : v === 'SQUAMOUS_CELL_CARCINOMA' ? 'Carcinom scuamocelular'
    : v === 'DERMATOFIBROMA' ? 'Dermatofibrom'
    : v === 'KERATOACANTHOMA' ? 'Keratoacantom'
    : v === 'ACTINIC_KERATOSIS' ? 'Keratoză actinică'
    : 'Alt tip',
}));

const Step11Diagnosis = () => {
  const { lesions, selectedLesionId, selectLesion, loading } = useDermatoWizard();
  const { user } = useAuthStore();
  const [diagnosis, setDiagnosis] = useState<Diagnosis | null>(null);
  const [saving, setSaving] = useState(false);
  const [formAntd] = Form.useForm<CreateDiagnosisDto>();

  const lesion = useMemo(
    () => lesions.find((l) => l.id === selectedLesionId) ?? null,
    [lesions, selectedLesionId]
  );

  useEffect(() => {
    if (!lesion?.id) {
      setDiagnosis(null);
      formAntd.resetFields();
      return;
    }
    let cancelled = false;
    const load = async () => {
      const v = await dermatoApi.diagnosis.getByLesion(lesion.id!).catch(() => null);
      if (cancelled) return;
      setDiagnosis(v);
      if (v) {
        formAntd.setFieldsValue({
          ...v,
          diagnosisDate: dayjs(v.diagnosisDate),
        } as any);
      } else {
        formAntd.resetFields();
        formAntd.setFieldsValue({
          diagnosisDate: dayjs(),
          diagnosis: '',
          tumorType: HistoTumorType.MELANOMA_MALIGNANT,
          location: lesion.location,
          diagnosedBy: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || '',
          conclusion: '',
          confirmed: false,
        } as any);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [lesion?.id, lesion?.location, formAntd, user]);

  const persist = async (): Promise<boolean> => {
    if (!lesion?.id) return true;
    try {
      const values = await formAntd.validateFields();
      setSaving(true);
      const dto: CreateDiagnosisDto = {
        ...values,
        diagnosisDate: (values.diagnosisDate as unknown as dayjs.Dayjs).toISOString(),
      };
      const saved = diagnosis?.id
        ? await dermatoApi.diagnosis.update(diagnosis.id, dto)
        : await dermatoApi.diagnosis.createForLesion(lesion.id, dto);
      setDiagnosis(saved);
      return true;
    } catch {
      // validation
      return false;
    } finally {
      setSaving(false);
    }
  };

  useRegisterStepSave(persist);

  if (loading) return <Card loading title="Diagnostic / Stadializare" />;

  if (lesions.length === 0) {
    return (
      <Card title="Diagnostic / Stadializare">
        <Empty description="Nu există leziuni. Mergi la pasul 3." />
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <MedicineBoxOutlined style={{ color: '#1890ff' }} />
          <span>Diagnostic final + Stadializare AJCC — 1:1 per leziune</span>
        </Space>
      }
    >
      <Form.Item label="Selectează leziunea" required style={{ marginBottom: 16 }}>
        <Select
          value={selectedLesionId ?? undefined}
          onChange={selectLesion}
          placeholder="Alege o leziune"
          options={lesions.map((l) => ({
            value: l.id,
            label: `${l.clinicalCode} — ${l.location}`,
          }))}
        />
      </Form.Item>

      {!lesion && <Alert type="warning" showIcon message="Selectează o leziune." />}

      {lesion && (
        <>
          <Alert
            type="info"
            showIcon
            message={
              <Space>
                <Text>Leziune:</Text>
                <Tag color="blue">{lesion.clinicalCode}</Tag>
                <Text strong>{lesion.location}</Text>
              </Space>
            }
            style={{ marginBottom: 16 }}
          />

          <Form form={formAntd} layout="vertical">
            <Form.Item name="diagnosisDate" label="Data diagnosticului" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="diagnosis" label="Diagnostic (text liber)" rules={[{ required: true }]}>
              <Input.TextArea rows={3} placeholder="ex: Melanom malign superficial spreading, Breslow 0.8 mm" />
            </Form.Item>
            <Form.Item name="tumorType" label="Tip tumoral" rules={[{ required: true }]}>
              <Select options={TUMOR_OPTIONS} />
            </Form.Item>
            <Form.Item name="location" label="Localizare" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="ajccStage" label="Stadiu AJCC">
              <Select options={AJCC_OPTIONS} allowClear />
            </Form.Item>
            <Form.Item name="breslowMm" label="Breslow (mm)">
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="ulceration" label="Ulcerație" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="classificationUsed" label="Clasificare folosită">
              <Input placeholder="ex: AJCC 8th edition" />
            </Form.Item>
            <Form.Item name="extent" label="Extensie">
              <Input.TextArea rows={2} placeholder="Detalii despre extensie / invazie…" />
            </Form.Item>
            <Form.Item name="diagnosedBy" label="Diagnosticat de" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="conclusion" label="Concluzie" rules={[{ required: true }]}>
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="confirmed" label="Confirmat" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Divider />
            <Form.Item>
              <Space>
                <Button type="primary" onClick={() => void persist()} loading={saving}>
                  {diagnosis ? 'Actualizează diagnostic' : 'Salvează diagnostic'}
                </Button>
                {diagnosis && <Text type="success">✓ Diagnostic existent — {diagnosis.id.slice(0, 8)}</Text>}
              </Space>
            </Form.Item>
          </Form>
        </>
      )}
    </Card>
  );
};

export default Step11Diagnosis;
